import {
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  useNavigate,
  useParams,
  type AnyRoute,
} from "@tanstack/react-router";
import { useEffect } from "react";
import type { ModuleManifest, SubRoute } from "@praesto/contracts";
import { AppLayout } from "./layouts/AppLayout";
import { TenantIndexRedirect } from "./components/TenantIndexRedirect";
import { ModuleIndexRedirect } from "./components/ModuleIndexRedirect";
import { PeopleHome } from "./people/PeopleHome";
import { PeopleList } from "./people/PeopleList";
import { StructureView } from "./people/StructureView";
import { ProfileView } from "./people/ProfileView";
import { ProfileInfoTab } from "./people/ProfileInfoTab";
import { ProfileSkillsTab } from "./people/ProfileSkillsTab";
import { ProfileAvailabilityTab } from "./people/ProfileAvailabilityTab";
import { ProfileLeavesTab } from "./people/ProfileLeavesTab";
import { ProfileWorkloadTab } from "./people/ProfileWorkloadTab";
import { WorkloadView } from "./people/WorkloadView";
import { LeavesView } from "./people/LeavesView";
import { SettingsHome } from "./settings/SettingsHome";
import { HelpPage } from "./help/HelpPage";
import { OnboardingWizard } from "./onboarding/OnboardingWizard";
import { useCore } from "./lib/core";

/**
 * Builds the TanStack Router instance from the active module manifests.
 * Called once after auth + tenant settings + manifests are loaded — the
 * router enumerates `subRoutes[]` per module and registers a route at
 * `/{tenantSlug}/{moduleId}/{subPath}`.
 *
 * Mademind console is a separate router built in router-mademind.ts.
 */
export function buildRouter(manifests: ReadonlyArray<ModuleManifest>) {
  const rootRoute = createRootRoute({
    component: () => <Outlet />,
  });

  const tenantRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/$tenantSlug",
    component: AppLayout,
  });

  const tenantIndexRoute = createRoute({
    getParentRoute: () => tenantRoute,
    path: "/",
    component: TenantIndexRedirect,
  });

  const peopleRoute = createRoute({
    getParentRoute: () => tenantRoute,
    path: "/people",
    component: PeopleHome,
  });

  // /people/ → managers get /list (team overview), staff get their own
  // profile (no manage:user permission means they can't access the
  // manager-level Lista / Workload / Ferie views).
  const peopleIndexRoute = createRoute({
    getParentRoute: () => peopleRoute,
    path: "/",
    component: function PeopleIndexRedirect() {
      const navigate = useNavigate();
      const core = useCore();
      const { tenantSlug } = useParams({ from: "/$tenantSlug" }) as {
        tenantSlug: string;
      };
      const canManage = core.rbac.can("manage", "user");
      const uid = core.auth.currentUserId();
      const target = canManage ? "list" : uid ? `${uid}/leaves` : "list";
      void navigate({ to: `/${tenantSlug}/people/${target}`, replace: true });
      return <p className="p-6 text-caption text-fg-muted">Reindirizzamento…</p>;
    },
  });

  const peopleListRoute = createRoute({
    getParentRoute: () => peopleRoute,
    path: "/list",
    component: function PeopleListRoute() {
      const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
      const navigate = useNavigate();
      return (
        <PeopleList
          onEdit={(uid) => void navigate({ to: `/${tenantSlug}/people/${uid}/info` as never })}
        />
      );
    },
  });

  const peopleStructureRoute = createRoute({
    getParentRoute: () => peopleRoute,
    path: "/structure",
    component: () => <StructureView />,
  });

  const peopleWorkloadRoute = createRoute({
    getParentRoute: () => peopleRoute,
    path: "/workload",
    component: WorkloadView,
  });

  const peopleLeavesRoute = createRoute({
    getParentRoute: () => peopleRoute,
    path: "/leaves",
    component: LeavesView,
  });

  const peopleProfileRoute = createRoute({
    getParentRoute: () => peopleRoute,
    path: "/$uid",
    component: ProfileView,
  });

  // Index redirect: /people/$uid → /people/$uid/info
  const peopleProfileIndexRoute = createRoute({
    getParentRoute: () => peopleProfileRoute,
    path: "/",
    component: function PeopleProfileIndexRedirect() {
      const { tenantSlug, uid } = useParams({ strict: false }) as {
        tenantSlug: string;
        uid: string;
      };
      const navigate = useNavigate();
      useEffect(() => {
        void navigate({
          to: `/${tenantSlug}/people/${uid}/info` as never,
          replace: true,
        });
      }, [tenantSlug, uid, navigate]);
      return null;
    },
  });

  // 5 tab placeholder routes (filled by Tasks 18-22)
  const peopleProfileInfoRoute = createRoute({
    getParentRoute: () => peopleProfileRoute,
    path: "/info",
    component: ProfileInfoTab,
  });

  const peopleProfileSkillsRoute = createRoute({
    getParentRoute: () => peopleProfileRoute,
    path: "/skills",
    component: ProfileSkillsTab,
  });

  const peopleProfileAvailabilityRoute = createRoute({
    getParentRoute: () => peopleProfileRoute,
    path: "/availability",
    component: ProfileAvailabilityTab,
  });

  const peopleProfileLeavesRoute = createRoute({
    getParentRoute: () => peopleProfileRoute,
    path: "/leaves",
    component: ProfileLeavesTab,
  });

  const peopleProfileWorkloadRoute = createRoute({
    getParentRoute: () => peopleProfileRoute,
    path: "/workload",
    component: ProfileWorkloadTab,
  });

  const settingsRoute = createRoute({
    getParentRoute: () => tenantRoute,
    path: "/settings",
    component: SettingsHome,
  });

  // The guide, read as a manual. Path in English (repo rule #7); the label
  // shown in the UI is "Guida", from the locales.
  const helpRoute = createRoute({
    getParentRoute: () => tenantRoute,
    path: "/help",
    component: HelpPage,
  });

  // Mandatory first-run wizard for a self-service owner — `OnboardingGuard`
  // (mounted in AppLayout) redirects here while
  // `settings.onboarding?.status === "pending"`. This route itself is the
  // guard's one exemption (no redirect loop).
  const onboardingRoute = createRoute({
    getParentRoute: () => tenantRoute,
    path: "/onboarding",
    component: OnboardingWizard,
  });

  // Build per-module routes: each module gets a root at /{moduleId}
  // with an index redirect (default sub-route) and one child per subRoute.
  const moduleRoutes: AnyRoute[] = [];
  for (const m of manifests) {
    const moduleRoot = createRoute({
      getParentRoute: () => tenantRoute,
      path: `/${m.id}`,
      component: Outlet,
    });

    const moduleIndex = createRoute({
      getParentRoute: () => moduleRoot,
      path: "/",
      component: () => <ModuleIndexRedirect manifest={m} />,
    });

    const subRoutes = (m.subRoutes ?? []).map((sub) => buildSubRoute(moduleRoot, sub));

    moduleRoutes.push(moduleRoot.addChildren([moduleIndex, ...subRoutes]));
  }

  const routeTree = rootRoute.addChildren([
    tenantRoute.addChildren([
      tenantIndexRoute,
      peopleRoute.addChildren([
        peopleIndexRoute,
        peopleListRoute,
        peopleStructureRoute,
        peopleWorkloadRoute,
        peopleLeavesRoute,
        peopleProfileRoute.addChildren([
          peopleProfileIndexRoute,
          peopleProfileInfoRoute,
          peopleProfileSkillsRoute,
          peopleProfileAvailabilityRoute,
          peopleProfileLeavesRoute,
          peopleProfileWorkloadRoute,
        ]),
      ]),
      settingsRoute,
      helpRoute,
      onboardingRoute,
      ...moduleRoutes,
    ]),
  ]);

  // Native cross-fade between pages: TanStack wraps each navigation in
  // document.startViewTransition() where supported (Chrome/Edge, Safari 18+).
  // Browsers without the View Transitions API fall back to an instant swap.
  // The transition is tuned (and disabled under prefers-reduced-motion) in
  // globals.css via ::view-transition-old/new(root).
  const router = createRouter({ routeTree, defaultViewTransition: true });
  return router;
}

function buildSubRoute(parent: AnyRoute, sub: SubRoute): AnyRoute {
  // Leaf route (no children): the sub.component is the route component.
  if (!sub.children || sub.children.length === 0) {
    return createRoute({
      getParentRoute: () => parent,
      path: `/${sub.path}`,
      component: sub.component as never,
    });
  }
  // Layout pattern (has children): the route is just a passthrough that
  // renders an Outlet. `sub.component` becomes the index child so that
  // visiting `/sub` still shows it, and child routes (`/sub/$id` etc.)
  // replace the Outlet content when navigated.
  const layoutRoute = createRoute({
    getParentRoute: () => parent,
    path: `/${sub.path}`,
    component: () => <Outlet />,
  });
  const indexRoute = createRoute({
    getParentRoute: () => layoutRoute,
    path: "/",
    component: sub.component as never,
  });
  const childRoutes = sub.children.map((c) => buildSubRoute(layoutRoute, c));
  return layoutRoute.addChildren([indexRoute, ...childRoutes]);
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof buildRouter>;
  }
}
